import {
  Card,
  Descriptions,
  Tag,
  Image,
  Typography,
  Space,
  Statistic,
  Row,
  Col,
  Divider,
  Button,
  message,
  Form,
  Input,
  Result,
} from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  AimOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import Loading from "../components/Loading";
import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  colorStatusWarning,
  formatDateTime,
  statusTextWarning,
} from "../utils/constants";

import { IMAGE_URL } from "../constants";
import { getWarning } from "../api/warmings/warningRequest";
import CancelWarning from "../components/CancelWarning";
import { countries } from "../utils/countries";
import PermissionRender from "../components/PermissionRender";
import WarningPdf from "../components/WarningDetail/WarningPdf";
import html2canvas from "html2canvas";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function WarningDetails() {
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [form] = Form.useForm();
  const { id } = useParams();
  const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const pageRef = useRef();

  useEffect(() => {
    fetchWarning();
  }, [id]);

  const fetchWarning = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const data = await getWarning(id);
      setWarning(data);
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 404) {
        setNotFound(true);
        message.error("L'avertissement demandé n'a pas été trouvé");
      } else {
        message.error(
          "Une erreur s'est produite lors du chargement de l'avertissement"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  

  

  const renderStatusInfo = () => {
    if (warning.status === "finalized" && warning?.infraction?.user) {
      return (
        <Card size="small" className="mb-6 border-green-500">
          <div className="flex items-center">
            <CheckCircleOutlined className="text-green-500 text-xl mr-2" />
            <div>
              <Text strong>Finalisée par :</Text>
              <div className="flex items-center mt-1">
                <UserOutlined className="text-green-500 mr-2" />
                <Text>{warning.infraction.user.full_name}</Text>
                <Text type="secondary" className="ml-2">
                  {formatDateTime(warning.infraction.datetime_infraction)}
                </Text>
              </div>
              <Text type="secondary" className="mt-1">
                Cet avertissement a été converti en infraction.
              </Text>
            </div>
          </div>
        </Card>
      );
    }

    if (warning.status === "canceled" && warning.cancel) {
      return (
        <Card size="small" className="mb-6 border-red-500">
          <div className="flex items-center">
            <StopOutlined className="text-red-500 text-xl mr-2" />
            <div>
              <Text strong>Annulée par:</Text>
              <div className="flex items-center mt-1">
                <UserOutlined className="text-red-500 mr-2" />
                <Text>{warning.cancel.full_name}</Text>
                <Text type="secondary" className="ml-2">
                  {formatDateTime(warning.cancelled_at)}
                </Text>
              </div>
              {warning.cancellation_reason && (
                <div className="mt-2">
                  <Text strong>Motif:</Text>
                  <Text className="ml-2">
                    {warning.cancellation_reason.name}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </Card>
      );
    }

    return null;
  };

  const handlePdfModalOpen = async () => {
    try {
      const element =
        pageRef.current || document.querySelector(".min-h-screen.bg-purple-50");
      if (element) {
        const canvas = await html2canvas(element, {
          useCORS: true,
          allowTaint: true,
          scale: 0.8,
          width: element.scrollWidth,
          height: element.scrollHeight,
        });
        setScreenshot(canvas.toDataURL("image/png"));
      }
      setIsPdfModalVisible(true);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
      setIsPdfModalVisible(true);
    }
  };

  const canCancelWarning =
    warning.status &&
    warning.status !== "canceled" &&
    warning.status !== "finalized";

  if (loading) return <Loading />;

  if (notFound) {
    return (
      <div className="min-h-screen bg-purple-50 p-8 flex justify-center">
        <Card className="max-w-lg w-full shadow-lg border-t-4 border-red-600">
          <Result
            status="404"
            title="Avertissement non trouvé"
            subTitle={`L'avertissement avec la référence #${id} n'existe pas ou a été supprimé.`}
            extra={
              <Button
                type="primary"
                onClick={() => window.history.back()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Retour
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-purple-50 p-8">
      <Card
        ref={pageRef}
        className="max-w-4xl mx-auto shadow-lg border-t-4 border-purple-600"
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Title level={2} className="text-purple-700 m-0">
              Détails de l'avertissement #{warning.reference}{" "}
              {warning.status === "finalized" && warning.infraction && (
                <Button
                  icon={<EyeOutlined />}
                  onClick={() =>
                    window.open(
                      `/infraction/${warning?.infraction?.id}`,
                      "_blank"
                    )
                  }
                />
              )}
            </Title>
            <Tag
              color={colorStatusWarning[warning.status]}
              className="text-lg px-4 py-1"
            >
              {statusTextWarning(warning.status)}
            </Tag>
          </div>
          <div className="flex gap-2">
            <Button
              type="default"
              icon={<PrinterOutlined />}
              size="small"
              onClick={handlePdfModalOpen}
              className="bg-purple-500 text-white hover:bg-purple-600"
            >
              Version Arabe
            </Button>
            {canCancelWarning && (
              <PermissionRender requiredPermission={["warnings", "cancel"]}>
                <CancelWarning warningId={id} onCancelled={fetchWarning} />
              </PermissionRender>
            )}
          </div>
        </div>
        {renderStatusInfo()}

        <Row gutter={24}>
          <Col span={14}>
            <Statistic
              title="Type d'Infraction"
              value={warning?.type_infraction?.name}
              valueStyle={{ color: "#722ed1" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Col>
        </Row>

        <Divider className="my-6" />

        <Descriptions column={1} className="mb-6">
          <Descriptions.Item
            label={
              <Text strong className="text-purple-700">
                Description
              </Text>
            }
          >
            {warning.description}
          </Descriptions.Item>
        </Descriptions>

        <Row gutter={24} className="mb-6">
          <Col span={12}>
            <Card
              size="small"
              title={
                <Text strong className="text-purple-700">
                  Informations du Véhicule
                </Text>
              }
              className="h-full"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Matricule">
                  {warning.matricule} {" ( "}
                  {
                    countries?.find(
                      (country) => country.code === warning?.vehicle?.country
                    )?.name
                  }
                  {" ) "}
                </Descriptions.Item>
                <Descriptions.Item label="Marque">
                  {warning.brand}
                </Descriptions.Item>
                <Descriptions.Item label="Modèle">
                  {warning.model}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={12}>
            <Card
              size="small"
              title={
                <Text strong className="text-purple-700">
                  Détails de l'Infraction
                </Text>
              }
              className="h-full"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <ClockCircleOutlined className="mr-2" />
                      <span>Date et Heure</span>
                    </div>
                  }
                >
                  {formatDateTime(warning.datetime_warning)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Contrat</span>
                    </div>
                  }
                >
                  {warning?.zone?.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Zone</span>
                    </div>
                  }
                >
                  {warning?.section?.name || "Non défini"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Rue</span>
                    </div>
                  }
                >
                  {warning?.street?.name || "Non défini"}
                  {" - "}
                  {warning?.street?.name_ar || "غير محدد"}
                </Descriptions.Item>

                <Descriptions.Item label="Coordonnées">
                  <a
                    href={`https://www.google.com/maps?q=${warning.latitude},${warning.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#722ed1", textDecoration: "underline" }}
                  >
                    Lat: {warning.latitude}, Long: {warning.longitude}
                  </a>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        <Card
          size="small"
          title={
            <Text strong className="text-purple-700">
              Preuves
            </Text>
          }
          className="mb-6"
        >
          <Space direction="vertical" size="middle" className="w-full">
            {warning.photos && (
              <div>
                <Text strong className="mr-3">
                  Photos:
                </Text>
                <Image.PreviewGroup>
                  <Space size={[8, 8]} wrap>
                    {warning.photos.split(",").map((photo, index) => (
                      <Image
                        key={index}
                        width={150}
                        height={150}
                        src={IMAGE_URL + photo}
                        alt={`Photo d'warning ${index + 1}`}
                        className="object-cover rounded"
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </div>
            )}
            <div className="flex justify-between items-center">
              <div>
                {warning.reserved_by_agent && (
                  <>
                    <Text strong>Prise en charge par:</Text>
                    <div className="flex items-center mt-2">
                      <UserOutlined className="text-blue-500 text-xl mr-2" />
                      <Text>{warning.reserved_by_agent.full_name}</Text>
                    </div>
                  </>
                )}
              </div>
              <div>
                <Text strong>Agent Verbalisateur:</Text>
                <div className="flex items-center mt-2">
                  <UserOutlined className="text-purple-600 text-xl mr-2" />
                  <Text>{warning?.user?.full_name}</Text>
                </div>
              </div>
            </div>
          </Space>
        </Card>

      </Card>
      <WarningPdf
        visible={isPdfModalVisible}
        onClose={() => setIsPdfModalVisible(false)}
        formValues={warning}
        screenshot={screenshot}
      />
    </div>
  );
}
